import React, { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AttendantRoomItem from '@hkm/components/Attendant/RoomList/Body/AttendantRoomItem/AttendantRoomItem';
import NoRoomItem from '@hkm/components/Attendant/RoomList/Body/NoRoom/NoRoomItem';
import { PendingQueueRoomsInfo } from '@hkm/components/Attendant/shared/components/PendingQueueRoomsInfo';
import { selectPendingRoomQueue } from '@hkm/components/Attendant/shared/domain/selectors';
import { disabledMenuSwipeClass } from '@hkm/components/Menu/MenuContainer';
import TaskProgressTypeBadge from '@hkm/components/shared/AttendantTaskProgressTypeBadge/AttendantTaskProgressTypeBadge';
import { AttendantRoom } from '@hkm/types/attendant/models/AttendantRoom';

import { AttendantTaskProgressType } from '@ac/library-api';
import {
  AcAnchorList,
  AcAnchorSection
} from '@ac/mobile-components/dist/components/anchor-list';
import { AcText } from '@ac/mobile-components/dist/components/text';
import { AcTileGroup } from '@ac/mobile-components/dist/components/tile';
import { Color, TextSize, TextWeight } from '@ac/mobile-components/dist/enums';

interface AttendantRoomListBodyProps {
  groupedRooms: Map<AttendantTaskProgressType, AttendantRoom[]>;
}

const AttendantRoomListBody: FC<AttendantRoomListBodyProps> = (
  props: AttendantRoomListBodyProps
) => {
  const prefix = 'attendant-room-list';
  const groupedRoomsArray = [...props.groupedRooms.keys()];
  const { t } = useTranslation();
  const pendingRoomQueue = useSelector(selectPendingRoomQueue);

  const renderListBasedOnCleaningStatus = (
    statusKey: AttendantTaskProgressType,
    key: number = 0
  ) => {
    const rooms = props.groupedRooms.get(statusKey)!;
    const isLast = key === groupedRoomsArray.length - 1;

    return (
      <AcAnchorSection
        to={statusKey}
        key={`${statusKey}${rooms.length}`}
        isLast={isLast}
        label={t(`ATTENDANT_ASSIGNMENTS.ROOM_LIST.ROOM_STATUS.${statusKey}`, {
          count: rooms.length
        })}
      >
        <div className={`${prefix}-section-header`}>
          <AcText
            color={Color.Black}
            weight={TextWeight.Semibold}
            size={TextSize.Main2}
          >
            <TaskProgressTypeBadge status={statusKey} />
          </AcText>
        </div>

        <AcTileGroup>
          {rooms.map((attendantRoom: AttendantRoom) => {
            return (
              <AttendantRoomItem
                key={attendantRoom.id}
                room={attendantRoom}
                taskType={statusKey}
              />
            );
          })}
        </AcTileGroup>

        {rooms.length === 0 && (
          <NoRoomItem roomStatus={statusKey} className="ac-spacing-bottom-md" />
        )}
      </AcAnchorSection>
    );
  };

  const renderList = useMemo(() => {
    return groupedRoomsArray.map(renderListBasedOnCleaningStatus);
  }, [props.groupedRooms]);

  return (
    <AcAnchorList
      navClass={disabledMenuSwipeClass}
      className={`${prefix}-navigation`}
    >
      {pendingRoomQueue.length > 0 && (
        <PendingQueueRoomsInfo
          count={pendingRoomQueue.length}
          showLink={false}
        />
      )}
      <div className={`${prefix}-section-list`}>{renderList}</div>
    </AcAnchorList>
  );
};

export default memo(AttendantRoomListBody);



// WEBPACK FOOTER //
// ./src/components/Attendant/RoomList/Body/AttendantRoomListBody.tsx